"use client";

import Rollbar from "rollbar";
import { ErrorBoundary as RollbarErrorBoundary, Provider } from "@rollbar/react";
import React from "react";
import type {
  ErrorTrackingProvider,
  ErrorTrackingConfig,
  ErrorContext,
  ErrorTrackingUser,
  ErrorBoundaryProps,
} from "../types";

let rollbarInstance: Rollbar | null = null;

export const RollbarProvider: ErrorTrackingProvider = {
  init(config: ErrorTrackingConfig) {
    if (typeof window === "undefined") {
      // Don't initialize on server-side
      return;
    }

    const rollbarConfig: Rollbar.Configuration = {
      accessToken: process.env.NEXT_PUBLIC_ROLLBAR_CLIENT_TOKEN || "",
      environment: config.environment,
      enabled: config.enabled && !!process.env.NEXT_PUBLIC_ROLLBAR_CLIENT_TOKEN,
      captureUncaught: config.captureUnhandledRejections ?? true,
      captureUnhandledRejections: config.captureUnhandledRejections ?? true,
      payload: {
        environment: config.environment,
        client: {
          javascript: {
            code_version: config.codeVersion,
            source_map_enabled: true,
            guess_uncaught_frames: true,
          },
        },
      },
    };

    rollbarInstance = new Rollbar(rollbarConfig);
  },

  logError(error: Error | unknown, context?: ErrorContext) {
    if (!rollbarInstance) return;

    if (error instanceof Error) {
      rollbarInstance.error(error, context);
    } else {
      rollbarInstance.error("Non-Error object thrown", { error, ...context });
    }
  },

  logWarning(message: string, context?: ErrorContext) {
    if (!rollbarInstance) return;
    rollbarInstance.warning(message, context);
  },

  logInfo(message: string, context?: ErrorContext) {
    if (!rollbarInstance) return;
    rollbarInstance.info(message, context);
  },

  setUser(user: ErrorTrackingUser | null) {
    if (!rollbarInstance) return;

    if (user) {
      rollbarInstance.configure({
        payload: {
          person: {
            id: user.id,
            email: user.email,
            username: user.name,
          },
          context: {
            organizationId: user.organizationId,
            role: user.role,
          },
        },
      });
    } else {
      // Clear user data by setting undefined
      rollbarInstance.configure({
        payload: {
          person: undefined,
        },
      });
    }
  },

  setContext(context: ErrorContext) {
    if (!rollbarInstance) return;
    rollbarInstance.configure({
      payload: {
        ...rollbarInstance.options.payload,
        ...context,
      },
    });
  },

  clearContext() {
    if (!rollbarInstance) return;
    // Reset to initial configuration
    rollbarInstance.configure({
      payload: {
        environment: rollbarInstance.options.payload?.environment,
        client: rollbarInstance.options.payload?.client,
      },
    });
  },

  ErrorBoundary: ({ children, fallback: FallbackComponent }: ErrorBoundaryProps) => {
    if (!rollbarInstance) {
      return <>{children}</>;
    }

    return (
      <Provider instance={rollbarInstance}>
        <RollbarErrorBoundary
          fallbackUI={FallbackComponent}
          errorMessage="Application error caught by error boundary"
        >
          {children}
        </RollbarErrorBoundary>
      </Provider>
    );
  },
};